import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GitBranch, GitPullRequest, GitMerge, Plus, RefreshCw, ExternalLink } from 'lucide-react';
import { beesClient, BeeResponse } from '../../api/beesClient.js';

interface ArcGitData {
  pullRequests: Array<{
    id: number;
    title: string;
    author: string;
    branch: string;
    status: 'open' | 'merged' | 'closed';
    mergeable: boolean;
    created_at: string;
    updated_at: string;
    url: string;
  }>;
  branches: Array<{
    name: string;
    is_default: boolean;
    last_commit: string;
    last_commit_date: string;
  }>;
  repository: {
    name: string;
    owner: string;
    default_branch: string;
    total_prs: number;
    open_prs: number;
  };
  lastSync: string;
  status: 'connected' | 'error' | 'syncing';
}

const ArcGitPanel: React.FC = () => {
  const [gitData, setGitData] = useState<ArcGitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<'list' | 'create' | 'merge'>('list');

  const fetchArcGitData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: BeeResponse = await beesClient.runArcGit({ 
        action: 'listPRs', 
        owner: 'DDPMLS', 
        repo: 'beehive-core' 
      });
      
      if (response.success && response.data) {
        setGitData(response.data as ArcGitData);
      } else {
        const mockData: ArcGitData = {
          pullRequests: [
            {
              id: 29,
              title: 'feat: implement SupervisorPanel UI panel',
              author: 'devin-ai',
              branch: 'devin/1751514378-supervisor-panel',
              status: 'open',
              mergeable: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              url: 'https://github.com/DDPMLS/beehive-core/pull/29'
            },
            {
              id: 28,
              title: 'feat: add missing beesClient methods',
              author: 'devin-ai',
              branch: 'devin/1751514378-missing-beesclient-methods',
              status: 'merged',
              mergeable: false,
              created_at: new Date(Date.now() - 86400000).toISOString(),
              updated_at: new Date(Date.now() - 3600000).toISOString(),
              url: 'https://github.com/DDPMLS/beehive-core/pull/28'
            }
          ],
          branches: [
            { name: 'main', is_default: true, last_commit: 'e5784f4', last_commit_date: new Date().toISOString() },
            { name: 'devin/1751517152-arcgitpanel', is_default: false, last_commit: 'e5784f4', last_commit_date: new Date().toISOString() }
          ],
          repository: {
            name: 'beehive-core',
            owner: 'DDPMLS',
            default_branch: 'main',
            total_prs: 29,
            open_prs: 1
          },
          lastSync: new Date().toISOString(),
          status: 'connected'
        };
        setGitData(mockData);
      }
    } catch (err) {
      setError('Failed to fetch Git data');
      console.error('ArcGit data fetch error:', err);
      
      const mockData: ArcGitData = {
        pullRequests: [],
        branches: [
          { name: 'main', is_default: true, last_commit: 'unknown', last_commit_date: new Date().toISOString() }
        ],
        repository: {
          name: 'beehive-core',
          owner: 'DDPMLS',
          default_branch: 'main',
          total_prs: 0,
          open_prs: 0
        },
        lastSync: new Date().toISOString(),
        status: 'error'
      };
      setGitData(mockData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArcGitData();
    const interval = setInterval(fetchArcGitData, 120000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: 'open' | 'merged' | 'closed') => {
    switch (status) {
      case 'open': return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'merged': return 'text-purple-400 bg-purple-900/20 border-purple-500/30';
      case 'closed': return 'text-red-400 bg-red-900/20 border-red-500/30';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
    }
  };

  const getStatusIcon = () => {
    if (!gitData) return <GitBranch className="w-5 h-5 text-gray-400" />;
    
    switch (gitData.status) {
      case 'connected':
        return <GitBranch className="w-5 h-5 text-green-400" />;
      case 'syncing':
        return <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />;
      case 'error':
        return <GitBranch className="w-5 h-5 text-red-400" />;
      default:
        return <GitBranch className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Git Operations
          </h2>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-slate-700 rounded mb-2"></div>
                <div className="h-3 bg-slate-700 rounded mb-3 w-1/2"></div>
                <div className="h-2 bg-slate-700 rounded"></div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Git Operations
          </h2>
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
            <div className="text-red-400 text-sm">⚠️ {error}</div>
            <button 
              onClick={fetchArcGitData} 
              className="text-xs text-red-300 hover:text-red-200 mt-2 underline flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!gitData) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Git Operations
          </h2>
          <div className="bg-gray-900/20 border border-gray-500/30 rounded-xl p-4">
            <div className="text-gray-400 text-sm">No Git data available</div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
          <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
          Git Operations
        </h2>

        <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <div>
                <h3 className="cream-gradient-text text-drop-shadow font-medium">
                  {gitData.repository.owner}/{gitData.repository.name}
                </h3>
                <p className="text-sm text-slate-400">
                  Last sync: {new Date(gitData.lastSync).toLocaleString()}
                </p>
              </div>
            </div>
            <button
              onClick={fetchArcGitData}
              className="p-2 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 rounded-lg text-indigo-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-400">{gitData.repository.open_prs}</div>
              <div className="text-sm text-slate-400">Open PRs</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-slate-300">{gitData.repository.total_prs}</div>
              <div className="text-sm text-slate-400">Total PRs</div>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSelectedAction('list')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedAction === 'list' 
                  ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/50' 
                  : 'bg-slate-800/50 text-slate-400 border border-slate-600/30 hover:bg-slate-700/50'
              }`}
            >
              <GitPullRequest className="w-4 h-4 inline mr-1" />
              Pull Requests
            </button>
            <button
              onClick={() => setSelectedAction('create')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedAction === 'create' 
                  ? 'bg-green-500/30 text-green-300 border border-green-500/50' 
                  : 'bg-slate-800/50 text-slate-400 border border-slate-600/30 hover:bg-slate-700/50'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              New PR
            </button>
          </div>
        </div>

        {selectedAction === 'list' && (
          <div className="space-y-3">
            <h3 className="text-md font-medium cream-gradient-text text-drop-shadow flex items-center gap-2">
              <GitPullRequest className="w-4 h-4 text-indigo-400" />
              Pull Requests ({gitData.pullRequests.length})
            </h3>
            {gitData.pullRequests.map((pr) => (
              <motion.div
                key={pr.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className={`border rounded-xl p-4 ${getStatusColor(pr.status)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-slate-800/50 px-2 py-1 rounded">
                      #{pr.id}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      pr.status === 'open' ? 'bg-green-900/30 text-green-300' :
                      pr.status === 'merged' ? 'bg-purple-900/30 text-purple-300' :
                      'bg-red-900/30 text-red-300'
                    }`}>
                      {pr.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pr.status === 'open' && pr.mergeable && (
                      <button className="violett-button text-drop-shadow px-2 py-1 text-xs">
                        <GitMerge className="w-3 h-3 inline mr-1" />
                        Merge
                      </button>
                    )}
                    <a 
                      href={pr.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-1 text-slate-400 hover:text-slate-300 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="font-medium text-sm">{pr.title}</div>
                  <div className="text-sm text-slate-300 mt-1">
                    by {pr.author} • {pr.branch} → {gitData.repository.default_branch}
                  </div>
                </div>
                <div className="text-xs text-slate-400">
                  Created: {new Date(pr.created_at).toLocaleString()} • 
                  Updated: {new Date(pr.updated_at).toLocaleString()}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {selectedAction === 'create' && (
          <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6">
            <h3 className="text-md font-medium cream-gradient-text text-drop-shadow mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-green-400" />
              Create New Pull Request
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium cream-gradient-text text-drop-shadow mb-2">Title</label>
                <input 
                  type="text" 
                  className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-lg w-full px-3 py-2 cream-gradient-text text-drop-shadow focus:border-indigo-500/50 focus:outline-none"
                  placeholder="Enter PR title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Branch</label>
                <select className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 font-medium hover:bg-slate-700 hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200">
                  {gitData.branches.filter(b => !b.is_default).map((branch) => (
                    <option key={branch.name} value={branch.name}>{branch.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium cream-gradient-text text-drop-shadow mb-2">Description</label>
                <textarea 
                  className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-lg w-full px-3 py-2 cream-gradient-text text-drop-shadow focus:border-indigo-500/50 focus:outline-none h-24"
                  placeholder="Describe your changes..."
                />
              </div>
              <button className="violett-button text-drop-shadow w-full px-4 py-2 font-medium">
                Create Pull Request
              </button>
            </div>
          </div>
        )}

        {gitData.pullRequests.length === 0 && selectedAction === 'list' && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-blue-400">
              <GitPullRequest className="w-5 h-5" />
              <span className="font-medium">No pull requests found</span>
            </div>
            <p className="text-sm text-blue-300 mt-1">
              Create a new pull request to get started with code reviews.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ArcGitPanel;
